import React, { useEffect, useState } from 'react';
import { Table, Button, Tag, Space, Popconfirm, message, Tooltip } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getLinks, deleteLink } from '../api';
import { colors } from '../theme';
import CreateLinkModal from './CreateLinkModal';
import EditLinkModal from './EditLinkModal';
import AuditModal from './AuditModal';

const SHORT_BASE = 'https://ai.quadreal.com/link';

export default function LinkTable({ getToken }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editLink, setEditLink] = useState(null);
  const [auditSlug, setAuditSlug] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const token = await getToken();
      setLinks(await getLinks(token));
    } catch {
      message.error('Failed to load links');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleDelete(slug) {
    try {
      const token = await getToken();
      await deleteLink(token, slug);
      message.success('Link deleted');
      load();
    } catch {
      message.error('Failed to delete link');
    }
  }

  const columns = [
    {
      title: 'Short URL',
      dataIndex: 'slug',
      ellipsis: true,
      render: (slug) => {
        const url = `${SHORT_BASE}/${slug}`;
        return (
          <Space>
            <Tooltip title={url}>
              <a href={url} target="_blank" rel="noreferrer" style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>{url}</a>
            </Tooltip>
            <Tooltip title="Copy">
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={() => { navigator.clipboard.writeText(url); message.success('Copied'); }}
              />
            </Tooltip>
          </Space>
        );
      }
    },
    {
      title: 'Target URL',
      dataIndex: 'target_url',
      ellipsis: true,
      render: (url) => <a href={url} target="_blank" rel="noreferrer">{url}</a>
    },
    { title: 'Owner', dataIndex: 'owner_upn', ellipsis: true },
    {
      title: 'Last edited',
      dataIndex: 'updated_at',
      render: (d) => dayjs(d).format('MMM D, YYYY')
    },
    { title: 'Clicks', dataIndex: 'click_count', align: 'right' },
    {
      title: 'Status',
      dataIndex: 'is_active',
      render: (v) => v ? <Tag color={colors.blue}>Active</Tag> : <Tag color={colors.neutral9}>Disabled</Tag>
    },
    {
      title: '',
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => setEditLink(record)}>Edit</Button>
          <Button size="small" onClick={() => setAuditSlug(record.slug)}>History</Button>
          <Popconfirm title="Delete this link?" onConfirm={() => handleDelete(record.slug)}>
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="primary" style={{ background: colors.blue }} onClick={() => setCreateOpen(true)}>
          New link
        </Button>
      </div>
      <Table
        rowKey="slug"
        columns={columns}
        dataSource={links}
        loading={loading}
        pagination={{ pageSize: 20 }}
      />
      <CreateLinkModal
        open={createOpen}
        getToken={getToken}
        onClose={() => { setCreateOpen(false); load(); }}
      />
      {editLink && (
        <EditLinkModal
          open={!!editLink}
          link={editLink}
          getToken={getToken}
          onClose={() => { setEditLink(null); load(); }}
        />
      )}
      {auditSlug && (
        <AuditModal
          open={!!auditSlug}
          slug={auditSlug}
          getToken={getToken}
          onClose={() => setAuditSlug(null)}
        />
      )}
    </>
  );
}
